import React, { useState, useRef } from "react";
import { api, errMsg } from "@/lib/api";
import { isVideo } from "@/lib/media";
import { toast } from "sonner";
import { UploadCloud, X, Loader2, Star, Play } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const toAbs = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);

export function MediaManager({ media = [], featured = "", onChange, testid = "media" }) {
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return toAbs(data.url);
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/") || f.type.startsWith("video/"));
    if (!files.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadOne(f));
      const newMedia = [...(media || []), ...urls];
      const newFeatured = featured || urls[0];
      onChange({ media: newMedia, featured: newFeatured });
      toast.success(urls.length > 1 ? `${urls.length} files uploaded` : "Uploaded");
    } catch (e) {
      toast.error(errMsg(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const removeAt = (i) => {
    const url = media[i];
    const newMedia = media.filter((_, idx) => idx !== i);
    const newFeatured = featured === url ? (newMedia[0] || "") : featured;
    onChange({ media: newMedia, featured: newFeatured });
  };

  const setFeatured = (url) => onChange({ media, featured: url });

  const onDrop = (e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files); };
  const onPaste = (e) => {
    const items = e.clipboardData?.items; if (!items) return;
    const files = [];
    for (const it of items) if (it.type.startsWith("image/") || it.type.startsWith("video/")) { const f = it.getAsFile(); if (f) files.push(f); }
    if (files.length) { e.preventDefault(); handleFiles(files); }
  };

  return (
    <div>
      <label className="text-sm font-medium text-slate-700 block mb-1.5">Photos & Videos</label>
      <p className="text-xs text-slate-400 mb-2">Upload as many as you like, then tap the <Star className="w-3 h-3 inline -mt-0.5 fill-orange-400 text-orange-400" /> star to choose the main one shown on your page.</p>

      {media?.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3" data-testid={`${testid}-grid`}>
          {media.map((url, i) => {
            const featuredHere = featured === url;
            return (
              <div key={i} className={`relative group rounded-xl overflow-hidden border-2 ${featuredHere ? "border-orange-500" : "border-slate-200"}`}>
                {isVideo(url) ? (
                  <div className="relative aspect-square bg-black">
                    <video src={url} className="w-full h-full object-cover" muted />
                    <Play className="absolute inset-0 m-auto w-6 h-6 text-white/90" />
                  </div>
                ) : (
                  <img src={url} alt="" className="aspect-square object-cover w-full" />
                )}
                {featuredHere && <span className="absolute bottom-1 left-1 bg-orange-500 text-white text-[10px] font-semibold rounded-full px-2 py-0.5">Main</span>}
                <button type="button" data-testid={`${testid}-feature-${i}`} onClick={() => setFeatured(url)} title="Set as main"
                  className={`absolute top-1 left-1 w-7 h-7 rounded-full flex items-center justify-center transition-colors ${featuredHere ? "bg-orange-500 text-white" : "bg-white/85 text-slate-600 hover:bg-orange-100"}`}>
                  <Star className={`w-3.5 h-3.5 ${featuredHere ? "fill-white" : ""}`} />
                </button>
                <button type="button" data-testid={`${testid}-remove-${i}`} onClick={() => removeAt(i)}
                  className="absolute top-1 right-1 w-7 h-7 rounded-full bg-slate-900/70 text-white flex items-center justify-center hover:bg-red-500">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div
        data-testid={`${testid}-dropzone`}
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onPaste={onPaste}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        className={`cursor-pointer rounded-2xl border-2 border-dashed transition-colors flex flex-col items-center justify-center text-center px-4 py-6 outline-none ${
          drag ? "border-orange-500 bg-orange-50" : "border-slate-300 hover:border-orange-400 bg-slate-50"
        }`}
      >
        {busy ? (
          <><Loader2 className="w-6 h-6 text-orange-500 animate-spin mb-2" /><span className="text-sm text-slate-500">Uploading…</span></>
        ) : (
          <>
            <UploadCloud className="w-7 h-7 text-orange-500 mb-2" strokeWidth={1.5} />
            <p className="text-sm font-medium text-slate-700">Drag & drop, paste, or click to add photos & videos</p>
            <p className="text-xs text-slate-400 mt-0.5">Images up to 8MB · Videos up to 128MB</p>
          </>
        )}
        <input ref={inputRef} type="file" accept="image/*,video/*" multiple className="hidden"
          data-testid={`${testid}-input`} onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
      </div>
    </div>
  );
}
